<template>
  <div ref="el" :class="$style.base">
    <div v-if="categories.length > 1" :class="$style.category">
      {{ data.id ? data.name : $t('services.top_services') }}
    </div>
    <div v-if="isServiceGroupVariations">
      <div
        v-for="serviceVariationGroup in (data as ServiceVariationGroupCategory)
          .service_variation_groups"
        :key="`service-variation-group-${serviceVariationGroup.id}`"
      >
        <SelectServiceVariationGroup
          :data="serviceVariationGroup"
          @scrollTo="$emit('scrollTo', $event)"
          @submit="$emit('submit')"
        />
      </div>
    </div>
    <div v-else>
      <SelectServicesItem
        v-for="(service, index) in (data as ServiceCategory).services"
        :key="index"
        :data="service"
        v-test="'service-item'"
        @scrollTo="$emit('scrollTo', $event)"
        @submit="$emit('submit')"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useServicesStore } from '@/stores/services';
import type { ServiceCategory, ServiceVariationGroupCategory } from '@/types';
import { storeToRefs } from 'pinia';
import { computed, inject, ref, watch } from 'vue';
import type { Ref } from 'vue';
import SelectServiceVariationGroup from './SelectServiceVariationGroup.vue';
import SelectServicesItem from './SelectServicesItem.vue';

const props = defineProps<{
  data: ServiceCategory | ServiceVariationGroupCategory;
}>();

const isServiceGroupVariations = computed(() => {
  return 'service_variation_groups' in props.data;
});

const emit = defineEmits(['submit', 'scrollTo', 'scrolledToTop']);
const clickedCategoryId = inject<any>('clickedCategoryId');

const { categories } = storeToRefs(useServicesStore());

const el = ref();

watch(clickedCategoryId, (value: number) => {
  if (value === props.data.id) {
    emit('scrollTo', el.value.offsetTop);
  }
});

const selectedCategoryId = inject<Ref<number>>('selectedCategoryId')!;
const scrollTop = inject<Ref<number>>('scrollTop')!;
const isAnimating = inject<Ref<boolean>>('isAnimating')!;

watch(scrollTop, (value) => {
  if (isAnimating.value || props.data.id === selectedCategoryId.value) {
    return;
  }

  if (
    value > el.value.offsetTop &&
    value < el.value.offsetTop + el.value.offsetHeight
  ) {
    emit('scrolledToTop', props.data.id);
  }
});
</script>

<style lang="scss" module>
.base {
  &:not(:first-child) {
    border-top: 1px solid $color-border;
  }
}

.category {
  font-size: 15px;
  line-height: 21px;
  padding: ($spacing * 2) $spacing 0;
  text-transform: uppercase;
  @include font-sub;
}
</style>
